import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard, Trash2, MapPin } from 'lucide-react'
import { usePaymentModalStore } from '../../store/paymentModalStore'
import {
  getSavedCards, getAddresses, deleteSavedCard, updateCardAddress,
  type SavedCardItem, type AddressItem,
} from '../../api/customerPayments'

export default function CardsPage() {
  const { t } = useTranslation('portal')
  const openPaymentModal = usePaymentModalStore((s) => s.openModal)
  const [cards, setCards] = useState<SavedCardItem[]>([])
  const [addresses, setAddresses] = useState<AddressItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    const [savedCards, addrs] = await Promise.all([getSavedCards(), getAddresses()])
    setCards(savedCards)
    setAddresses(addrs)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (cardId: string) => {
    setError(null)
    const ok = await deleteSavedCard(cardId)
    if (ok) {
      setCards((prev) => prev.filter((c) => c.id !== cardId))
    } else {
      setError(t('dashboard.cardDeleteFailed', 'Failed to delete card'))
    }
  }

  const handleChangeAddress = async (cardId: string, addressId: string | null) => {
    setError(null)
    const ok = await updateCardAddress(cardId, addressId)
    if (ok) {
      setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, address_id: addressId } : c)))
    } else {
      setError(t('dashboard.cardUpdateFailed', 'Failed to update card'))
    }
  }

  const brandIcon = (brand: string) => {
    const b = (brand || '').toLowerCase()
    if (b.includes('visa')) return 'Visa'
    if (b.includes('master')) return 'Mastercard'
    if (b.includes('maestro')) return 'Maestro'
    return brand || 'Card'
  }

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('dashboard.manageCards')}</h1>
        <p className="text-slate-500">{t('dashboard.loading', 'Loading...')}</p>
      </div>
    )
  }

  if (cards.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('dashboard.manageCards')}</h1>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-slate-500" />
          </div>
          <p className="text-slate-600 mb-6">{t('dashboard.noSavedCards')}</p>
          <p className="text-sm text-slate-500 mb-6">{t('dashboard.cardsHint')}</p>
          <button
            type="button"
            onClick={() => openPaymentModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('dashboard.payInvoice')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('dashboard.manageCards')}</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">{error}</div>
      )}

      <div className="space-y-3">
        {cards.map((card) => {
          return (
            <div key={card.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <CreditCard className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">{brandIcon(card.card_brand)}</span>
                    <span className="text-slate-700 font-mono">···· {card.last4}</span>
                    {card.expiration_date && (
                      <span className="text-xs text-slate-500">{card.expiration_date}</span>
                    )}
                  </div>

                  {/* Address selector */}
                  <div className="flex items-center gap-2 mt-3">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <select
                      value={card.address_id || ''}
                      onChange={(e) => handleChangeAddress(card.id, e.target.value || null)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('dashboard.noAddress', 'No address linked')}</option>
                      {addresses.map((addr) => (
                        <option key={addr.id} value={addr.id}>
                          {addr.full_name} — {addr.address}, {addr.city}
                          {addr.is_default ? ` (${t('payment.address.default_label', 'default')})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button type="button" onClick={() => handleDelete(card.id)}
                  title={t('dashboard.deleteCard', 'Delete card')}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition ml-4">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
